import React from "react";
import { Card, CardContent } from "../ui/card";
import { Input } from "../ui/input";
import { FileCheck, ClipboardList, Wrench, CheckCircle2, Package, User, Search } from "lucide-react";

/**
 * ProvisionStats — Contadores de estado (filtros rápidos) y buscador de provisiones.
 *
 * Extraído 1:1 desde `pages/ProvisionsPage.js`. Cada tarjeta actúa como filtro toggle por
 * estado (Comercial / Administrativa / Técnica / Completadas / Total / Mis Asignadas);
 * la tarjeta activa se resalta con un anillo. Incluye el input de búsqueda por cliente/ID/ubicación.
 *
 * @param {Object}   props.stats            Totales por estado ({ pendiente_comercial, ..., total }).
 * @param {string}   props.statusFilter     Filtro de estado activo ("all" | estado | "MIS_ASIGNADAS").
 * @param {Function} props.setStatusFilter  Cambia/limpia el filtro de estado (toggle).
 * @param {number}   props.myAssignedCount  Nº de provisiones asignadas al usuario actual.
 * @param {string}   props.searchTerm       Término de búsqueda actual.
 * @param {Function} props.setSearchTerm    Setter del término de búsqueda.
 */
export const ProvisionStats = ({
  stats,
  statusFilter,
  setStatusFilter,
  myAssignedCount,
  searchTerm,
  setSearchTerm,
}) => {
  return (
    <>
      {/* Status Counters */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card 
          className={`bg-[#0A0A35] border-[#1E1E5F] cursor-pointer transition-all ${statusFilter === "PENDIENTE_COMERCIAL" ? "ring-2 ring-blue-500" : ""}`}
          onClick={() => setStatusFilter(statusFilter === "PENDIENTE_COMERCIAL" ? "all" : "PENDIENTE_COMERCIAL")}
        >
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <FileCheck className="h-5 w-5 text-blue-400" />
              </div>
              <div>
                <p className="text-slate-400 text-xs">Comercial</p>
                <p className="text-xl font-bold text-blue-400">{stats.pendiente_comercial || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card 
          className={`bg-[#0A0A35] border-[#1E1E5F] cursor-pointer transition-all ${statusFilter === "PENDIENTE_ADMINISTRATIVA" ? "ring-2 ring-yellow-500" : ""}`}
          onClick={() => setStatusFilter(statusFilter === "PENDIENTE_ADMINISTRATIVA" ? "all" : "PENDIENTE_ADMINISTRATIVA")}
        >
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                <ClipboardList className="h-5 w-5 text-yellow-400" />
              </div>
              <div>
                <p className="text-slate-400 text-xs">Administrativa</p>
                <p className="text-xl font-bold text-yellow-400">{stats.pendiente_administrativa || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card 
          className={`bg-[#0A0A35] border-[#1E1E5F] cursor-pointer transition-all ${statusFilter === "PENDIENTE_TECNICA" ? "ring-2 ring-orange-500" : ""}`}
          onClick={() => setStatusFilter(statusFilter === "PENDIENTE_TECNICA" ? "all" : "PENDIENTE_TECNICA")}
        >
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
                <Wrench className="h-5 w-5 text-orange-400" />
              </div>
              <div>
                <p className="text-slate-400 text-xs">Técnica</p>
                <p className="text-xl font-bold text-orange-400">{stats.pendiente_tecnica || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card 
          className={`bg-[#0A0A35] border-[#1E1E5F] cursor-pointer transition-all ${statusFilter === "COMPLETADA" ? "ring-2 ring-green-500" : ""}`}
          onClick={() => setStatusFilter(statusFilter === "COMPLETADA" ? "all" : "COMPLETADA")}
        >
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                <CheckCircle2 className="h-5 w-5 text-green-400" />
              </div>
              <div>
                <p className="text-slate-400 text-xs">Completadas</p>
                <p className="text-xl font-bold text-green-400">{stats.completada || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card 
          className={`bg-[#0A0A35] border-[#1E1E5F] cursor-pointer transition-all ${statusFilter === "all" ? "ring-2 ring-[#03BEE4]" : ""}`}
          onClick={() => setStatusFilter("all")}
        >
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#03BEE4]/20 flex items-center justify-center">
                <Package className="h-5 w-5 text-[#03BEE4]" />
              </div>
              <div>
                <p className="text-slate-400 text-xs">Total</p>
                <p className="text-xl font-bold text-[#03BEE4]">{stats.total || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card 
          className={`bg-[#0A0A35] border-[#1E1E5F] cursor-pointer transition-all ${statusFilter === "MIS_ASIGNADAS" ? "ring-2 ring-purple-500" : ""}`}
          onClick={() => setStatusFilter(statusFilter === "MIS_ASIGNADAS" ? "all" : "MIS_ASIGNADAS")}
        >
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                <User className="h-5 w-5 text-purple-400" />
              </div>
              <div>
                <p className="text-slate-400 text-xs">Mis Asignadas</p>
                <p className="text-xl font-bold text-purple-400">{myAssignedCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="flex gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Buscar por cliente, ID o ubicación..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-[#0A0A35] border-[#1E1E5F] text-white"
          />
        </div>
      </div>
    </>
  );
};
